import { getDefaultConfig } from '@rainbow-me/rainbowkit';
import {
  braveWallet,
  coinbaseWallet,
  injectedWallet,
  metaMaskWallet,
  phantomWallet,
  rabbyWallet,
  rainbowWallet,
  trustWallet,
  walletConnectWallet,
  zerionWallet,
} from '@rainbow-me/rainbowkit/wallets';
import { QueryClient } from '@tanstack/react-query';
import { HttpTransport } from 'viem';
import { http } from 'wagmi';
import {
  arbitrum,
  avalanche,
  base,
  bsc,
  mainnet,
  optimism,
  polygon,
  foundry,
  scroll,
} from 'wagmi/chains';

import { ClientConfigurationError } from '@/lib/errors/client';

import { SUPPORTED_CHAINS } from '@/constants/chain';
import { JSON_RPC_HTTP_URL_BY_CHAIN_ID } from '@/constants/rpc';

// utils
import { isTest } from '@/utils/environment';

if (!process.env.NEXT_PUBLIC_WALLETCONNECT_PROJECT_ID) {
  throw new ClientConfigurationError('A WalletConnect project ID is required.');
}

const projectId = process.env.NEXT_PUBLIC_WALLETCONNECT_PROJECT_ID;

const needsInjectedWalletFallback =
  typeof window !== 'undefined' &&
  window.ethereum &&
  !window.ethereum.isMetaMask &&
  !window.ethereum.isPhantom &&
  !window.ethereum.isRabby &&
  !window.ethereum.isRainbow &&
  !(window.ethereum.isTrust || window.ethereum.isTrustWallet) &&
  !window.ethereum.isZerion &&
  !window.ethereum.isCoinbaseWallet &&
  !window.ethereum.isBraveWallet;

// configure wallet connectors
const wallets = [
  {
    groupName: 'Recommended',
    wallets: [
      // order is display order in UI
      braveWallet,
      coinbaseWallet,
      metaMaskWallet,
      phantomWallet,
      rainbowWallet,
      rabbyWallet,
      trustWallet,
      zerionWallet,
      walletConnectWallet,
      ...(needsInjectedWalletFallback ? [injectedWallet] : []),
    ],
  },
];

const queryClient = new QueryClient();

const config = getDefaultConfig({
  appName: 'matcha.xyz',
  chains: [
    mainnet,
    polygon,
    bsc,
    arbitrum,
    optimism,
    avalanche,
    base,
    scroll,
    ...(isTest ? [foundry] : []),
  ],
  projectId,
  wallets,
  transports: SUPPORTED_CHAINS.reduce<{ [chainId: string]: HttpTransport }>((obj, chain) => {
    obj[chain.id] = http(JSON_RPC_HTTP_URL_BY_CHAIN_ID[chain.id]);
    return obj;
  }, {}),
  ssr: true,
});

const wagmiConfig = {
  config,
  queryClient,
};

export default wagmiConfig;
